import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useSpring } from "framer-motion";

import { clampQrOffset, qrCursorTarget } from "@/lib/living-qr-display";

const SPRING = { stiffness: 12, damping: 16, mass: 1.1, restDelta: 0.2, restSpeed: 0.5 };

export function useQrCursorLag({ stageRef, homeRef, dragX, dragY, disabled, paused, size }) {
  const x = useSpring(0, SPRING);
  const y = useSpring(0, SPRING);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const freeze = useCallback(() => {
    x.jump(Math.round(x.get()));
    y.jump(Math.round(y.get()));
  }, [x, y]);

  const constrain = useCallback(() => {
    const frame = homeRef.current?.getBoundingClientRect();
    const stage = stageRef.current?.getBoundingClientRect();
    if (!frame?.width || !stage?.width) return;
    const next = clampQrOffset({ x: x.get(), y: y.get() }, frame, stage, { x: dragX.get(), y: dragY.get() });
    x.jump(next.x);
    y.jump(next.y);
  }, [dragX, dragY, homeRef, stageRef, x, y]);

  useLayoutEffect(() => {
    if (disabled || paused) freeze();
    constrain();
  }, [constrain, disabled, freeze, paused, size]);

  useEffect(() => {
    let frameRequest = null;
    let pointer = null;
    const update = () => {
      frameRequest = null;
      if (!pointer || pausedRef.current || document.visibilityState === "hidden") return;
      const frame = homeRef.current?.getBoundingClientRect();
      const stage = stageRef.current?.getBoundingClientRect();
      if (!frame?.width || !stage?.width) return;
      const drag = { x: dragX.get(), y: dragY.get() };
      const left = frame.left + drag.x + x.get();
      const top = frame.top + drag.y + y.get();
      // Yield before the pointer reaches the control, so it is easy to catch/click.
      if (pointer.x >= left - 80 && pointer.x <= left + frame.width + 80
        && pointer.y >= top - 80 && pointer.y <= top + frame.height + 80) {
        freeze();
        return;
      }
      const target = qrCursorTarget(pointer, frame, stage, drag);
      x.set(target.x);
      y.set(target.y);
    };
    const onPointerMove = (event) => {
      if (disabled || pausedRef.current || event.pointerType !== "mouse" || window.innerWidth < 960) return;
      pointer = { x: event.clientX, y: event.clientY };
      if (frameRequest === null) frameRequest = window.requestAnimationFrame(update);
    };
    const onResize = () => constrain();
    if (!disabled) window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.cancelAnimationFrame(frameRequest);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", onResize);
      freeze();
    };
  }, [constrain, disabled, dragX, dragY, freeze, homeRef, stageRef, x, y]);

  return { x, y, freeze };
}
